import React, { useState, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, Bell } from "lucide-react";
import Sidebar from "./Sidebar";
import Profile from "./Profile";
import { useAuth } from "../contexts/AuthContext";
import { walletService } from "../services/walletService";
import { parseMoney } from "../utils/money";

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const normalizeBalances = useCallback(
    (balances = {}) => ({
      affordable_data_balance: parseMoney(balances.affordable_data_balance),
      airtime_data_balance: parseMoney(balances.airtime_data_balance),
      commission_rate: parseMoney(balances.commission_rate),
    }),
    [],
  );

  // Fresh wallet balances from API
  const [walletBalances, setWalletBalances] = useState({
    ...normalizeBalances(user),
  });

  // Fetch fresh balance from backend
  const fetchBalance = useCallback(async () => {
    try {
      const response = await walletService.getBalance();
      if (response.status === "success") {
        setWalletBalances(normalizeBalances(response.data));
      }
    } catch (err) {
      console.log("Balance fetch error:", err);
    }
  }, [normalizeBalances]);

  // Fetch balance when component loads
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const handleLogout = async () => {
    try {
      logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      logout();
      navigate("/login", { replace: true });
    }
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      "/dashboard": "Dashboard",
      "/dashboard/deposits": "Deposits",
      "/dashboard/products/affordable-data": "Affordable Data",
      "/dashboard/products/airtime-data": "Airtime & Data",
      "/dashboard/history": "History",
    };
    return titles[path] || "Dashboard";
  };

  // Scroll listener to change header background
  useEffect(() => {
    const handleScroll = (e) => {
      const scrollTop = e.target.scrollTop;
      setIsScrolled(scrollTop > 20);
    };

    const contentContainer = document.getElementById("content-container");
    if (contentContainer) {
      contentContainer.addEventListener("scroll", handleScroll);
      return () => contentContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Get user's first name for display
  const userFirstName = user?.first_name || "User";

  return (
    <div className="h-screen bg-gray-100 overflow-hidden">
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        handleLogout={handleLogout}
      />

      {/* Main Content Area */}
      <div className="lg:ml-72 h-screen flex flex-col">
        {/* Floating Header */}
        <div className="flex-shrink-0 z-30 px-2 pt-2 pb-2">
          <header
            className={`flex items-center justify-between px-6 py-4 rounded-3xl transition-all duration-300 ${
              isScrolled
                ? "bg-white shadow-lg backdrop-blur-lg"
                : "bg-transparent"
            }`}
          >
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden py-2 px-1 rounded-xl hover:bg-gray-100"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {getPageTitle()}
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">
                  Let's be productive today
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 rounded-xl hover:bg-gray-100 transition-colors">
                <Bell className="w-5 h-5 text-gray-600" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">3</span>
                </div>
              </button>

              {/* Profile Avatar Button */}
              <button
                onClick={() => setShowProfile(true)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:scale-105 transition-transform duration-200 shadow-sm"
                style={{ backgroundColor: "#FFD700" }}
              >
                <span className="text-black font-bold text-sm">
                  {userFirstName.charAt(0).toUpperCase()}
                  {user?.last_name?.charAt(0).toUpperCase() || ""}
                </span>
              </button>
            </div>
          </header>
        </div>

        {/* Content Container */}
        <div className="flex-1 px-2 pb-6 overflow-hidden">
          <div
            className="bg-white shadow-lg h-full overflow-hidden pt-3"
            style={{ borderRadius: "2.5rem" }}
          >
            {/* Scrollable Content */}
            <div
              id="content-container"
              className="h-full overflow-y-auto"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <div className="py-8 px-4 space-y-8">
                {/* This is where the different pages will render */}
                <Outlet context={{ walletBalances, fetchBalance }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Component */}
      <Profile
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        user={user}
        walletBalances={walletBalances}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default DashboardLayout;
